import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Bell, Mail, MessageSquare, Info } from 'lucide-react';

const Toggle = ({ checked, onChange, disabled = false }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    onClick={() => !disabled && onChange(!checked)}
    className={`relative inline-flex h-5 w-9 flex-shrink-0 rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out
      focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2
      ${checked ? "bg-primary-600" : "bg-gray-200"}
      ${disabled ? "opacity-40 cursor-not-allowed" : "cursor-pointer"}`}
  >
    <span
      className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out
        ${checked ? "translate-x-4" : "translate-x-0"}`}
    />
  </button>
);

const NotificationsSection = () => {
  const { t } = useTranslation();

  const [settings, setSettings] = useState([
    {
      key: "sellerApp",
      title: t('settings.notifSellerAppTitle'),
      description: t('settings.notifSellerAppDesc'),
      email: false,
      sms: false,
    },
    {
      key: "docs",
      title: t('settings.notifDocsTitle'),
      description: t('settings.notifDocsDesc'),
      email: true,
      sms: true,
    },
    {
      key: "payments",
      title: t('settings.notifPaymentsTitle'),
      description: t('settings.notifPaymentsDesc'),
      email: true,
      sms: false,
    },
  ]);

  const toggle = (key, channel) => {
    setSettings(prev =>
      prev.map(s => s.key === key ? { ...s, [channel]: !s[channel] } : s)
    );
  };

  return (
    <div className="space-y-4 max-w-2xl">
      <div className="bg-white border border-gray-200 rounded-xl p-6">
        {/* Header */}
        <div className="flex items-start gap-3 mb-2">
          <div className="flex-shrink-0 w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center mt-0.5">
            <Bell className="w-4 h-4 text-gray-600" />
          </div>
          <div className="flex-1">
            <h3 className="text-sm font-semibold text-gray-900">{t('settings.notificationsTitle')}</h3>
            <p className="text-xs text-gray-500 mt-0.5">{t('settings.manageNotificationsDesc')}</p>
          </div>
        </div>

        {/* Coming soon notice */}
        <div className="flex items-start gap-2 px-3 py-2.5 bg-blue-50 border border-blue-100 rounded-lg mb-5 mt-4">
          <Info className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
          <p className="text-xs text-blue-700 font-medium">{t('settings.notificationsComingSoon')}</p>
        </div>

        {/* Column headers */}
        <div className="flex items-center mb-2 px-1">
          <span className="flex-1 text-xs font-medium text-gray-400">{t('settings.generalNotificationSettings')}</span>
          <div className="flex items-center gap-4 flex-shrink-0">
            <div className="flex items-center gap-1 w-16 justify-center">
              <Mail className="w-3.5 h-3.5 text-gray-400" />
              <span className="text-xs text-gray-500">{t('settings.emailLabel')}</span>
            </div>
            <div className="flex items-center gap-1 w-16 justify-center">
              <MessageSquare className="w-3.5 h-3.5 text-gray-400" />
              <span className="text-xs text-gray-500">{t('settings.smsLabel')}</span>
            </div>
          </div>
        </div>

        {/* Notification rows */}
        <div className="divide-y divide-gray-100">
          {settings.map(item => (
            <div key={item.key} className="flex items-center justify-between py-3 px-1">
              <div className="flex-1 pr-4 min-w-0">
                <p className="text-sm font-medium text-gray-900">{item.title}</p>
                {item.description && (
                  <p className="text-xs text-gray-500 mt-0.5 truncate">{item.description}</p>
                )}
              </div>
              <div className="flex items-center gap-4 flex-shrink-0">
                <div className="w-16 flex justify-center">
                  <Toggle checked={item.email} onChange={() => toggle(item.key, "email")} disabled />
                </div>
                <div className="w-16 flex justify-center">
                  <Toggle checked={item.sms} onChange={() => toggle(item.key, "sms")} disabled />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default NotificationsSection;
